// src/features/auth/token-management/storage/TokenStorage.ts

import { SecureStorage } from './SecureStorage';
import { TokenPair } from '../../types/auth.types';
import { MemoryCache } from './MemoryCache';
import { CookieStorage } from './CookieStorage';

export interface TokenStorageConfig {
  storageType?: 'memory' | 'localStorage' | 'sessionStorage' | 'cookie';
  enableEncryption?: boolean;
  tokenPrefix?: string;
  cookieOptions?: {
    domain?: string;
    path?: string;
    secure?: boolean;
    sameSite?: 'strict' | 'lax' | 'none';
  };
}

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  tokenType: string;
  scope?: string;
}

export class TokenStorage {
  private config: Required<TokenStorageConfig>;
  private memoryCache: MemoryCache<StoredTokens>;
  private secureStorage?: SecureStorage;
  private cookieStorage?: CookieStorage;
  private readonly ACCESS_TOKEN_KEY: string;
  private readonly REFRESH_TOKEN_KEY: string;
  private readonly TOKEN_METADATA_KEY: string;

  constructor(config: TokenStorageConfig = {}) {
    this.config = {
      storageType: 'memory',
      enableEncryption: true,
      tokenPrefix: 'auth',
      cookieOptions: {
        domain: undefined,
        path: '/',
        secure: true,
        sameSite: 'strict',
      },
      ...config,
    };

    // Initialize storage keys
    this.ACCESS_TOKEN_KEY = `${this.config.tokenPrefix}_access_token`;
    this.REFRESH_TOKEN_KEY = `${this.config.tokenPrefix}_refresh_token`;
    this.TOKEN_METADATA_KEY = `${this.config.tokenPrefix}_token_metadata`;

    // Initialize storage backends
    this.memoryCache = new MemoryCache<StoredTokens>();
    
    if (this.config.enableEncryption && this.config.storageType !== 'cookie') {
      this.secureStorage = new SecureStorage();
    }

    if (this.config.storageType === 'cookie') {
      this.cookieStorage = new CookieStorage(this.config.cookieOptions);
    }
  }

  /**
   * Store token pair
   */
  async store(tokens: TokenPair): Promise<void> {
    const storedTokens: StoredTokens = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: Date.now() + (tokens.expiresIn * 1000),
      tokenType: tokens.tokenType || 'Bearer',
      scope: tokens.scope,
    };

    // Always cache in memory for fast access
    this.memoryCache.set('tokens', storedTokens, tokens.expiresIn * 1000);

    // Store in persistent storage
    switch (this.config.storageType) {
      case 'memory':
        // Already stored in memory cache
        break;

      case 'cookie':
        await this.storeToCookies(storedTokens);
        break;

      case 'localStorage':
      case 'sessionStorage':
        await this.storeToWebStorage(storedTokens);
        break;
    }

    // Emit storage event for cross-tab synchronization
    this.emitStorageEvent('store', storedTokens);
  }

  /**
   * Get access token
   */
  async getAccessToken(): Promise<string | null> {
    // Check memory cache first
    const cached = this.memoryCache.get('tokens');
    if (cached && !this.isTokenExpired(cached)) {
      return cached.accessToken;
    }

    // Retrieve from persistent storage
    const stored = await this.retrieve();
    if (stored && !this.isTokenExpired(stored)) {
      // Update memory cache
      this.memoryCache.set('tokens', stored);
      return stored.accessToken;
    }

    return null;
  }

  /**
   * Get refresh token
   */
  async getRefreshToken(): Promise<string | null> {
    // Check memory cache first
    const cached = this.memoryCache.get('tokens');
    if (cached) {
      return cached.refreshToken;
    }

    // Retrieve from persistent storage
    const stored = await this.retrieve();
    return stored?.refreshToken || null;
  }

  /**
   * Retrieve stored tokens
   */
  async retrieve(): Promise<StoredTokens | null> {
    // Check memory cache first
    const cached = this.memoryCache.get('tokens');
    if (cached) {
      return cached;
    }

    // Retrieve from persistent storage
    switch (this.config.storageType) {
      case 'memory':
        return null;

      case 'cookie':
        return this.retrieveFromCookies();

      case 'localStorage':
      case 'sessionStorage':
        return this.retrieveFromWebStorage();

      default:
        return null;
    }
  }

  /**
   * Clear stored tokens
   */
  async clear(): Promise<void> {
    // Clear memory cache
    this.memoryCache.clear();

    // Clear persistent storage
    switch (this.config.storageType) {
      case 'cookie':
        this.cookieStorage?.remove(this.ACCESS_TOKEN_KEY);
        this.cookieStorage?.remove(this.REFRESH_TOKEN_KEY);
        this.cookieStorage?.remove(this.TOKEN_METADATA_KEY);
        break;

      case 'localStorage':
        localStorage.removeItem(this.ACCESS_TOKEN_KEY);
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
        localStorage.removeItem(this.TOKEN_METADATA_KEY);
        break;

      case 'sessionStorage':
        sessionStorage.removeItem(this.ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(this.TOKEN_METADATA_KEY);
        break;
    }

    // Emit storage event for cross-tab synchronization
    this.emitStorageEvent('clear', null);
  }

  /**
   * Rotate tokens (update with new tokens)
   */
  async rotate(oldRefreshToken: string, newTokens: TokenPair): Promise<void> {
    // Verify old refresh token matches
    const current = await this.retrieve();
    if (current?.refreshToken !== oldRefreshToken) {
      throw new Error('Token rotation failed: refresh token mismatch');
    }

    // Store new tokens
    await this.store(newTokens);

    // Emit rotation event
    this.emitStorageEvent('rotate', newTokens);
  }

  /**
   * Check if tokens exist
   */
  async hasTokens(): Promise<boolean> {
    const tokens = await this.retrieve();
    return tokens !== null && !this.isTokenExpired(tokens);
  }

  /**
   * Get token expiry time
   */
  async getTokenExpiry(): Promise<number | null> {
    const tokens = await this.retrieve();
    return tokens?.expiresAt || null;
  }

  /**
   * Check if token is expired
   */
  private isTokenExpired(tokens: StoredTokens): boolean {
    // Add 60 second buffer to account for clock skew
    return Date.now() >= (tokens.expiresAt - 60000);
  }

  /**
   * Store tokens to cookies
   */
  private async storeToCookies(tokens: StoredTokens): Promise<void> {
    if (!this.cookieStorage) return;

    // Store access token in httpOnly cookie
    this.cookieStorage.set(this.ACCESS_TOKEN_KEY, tokens.accessToken, {
      expires: new Date(tokens.expiresAt),
      httpOnly: true,
      ...this.config.cookieOptions,
    });

    // Store refresh token in separate httpOnly cookie
    this.cookieStorage.set(this.REFRESH_TOKEN_KEY, tokens.refreshToken, {
      httpOnly: true,
      ...this.config.cookieOptions,
    });

    // Store metadata in regular cookie for client access
    this.cookieStorage.set(this.TOKEN_METADATA_KEY, JSON.stringify({
      expiresAt: tokens.expiresAt,
      tokenType: tokens.tokenType,
      scope: tokens.scope,
    }), {
      expires: new Date(tokens.expiresAt),
      ...this.config.cookieOptions,
      httpOnly: false,
    });
  }

  /**
   * Retrieve tokens from cookies
   */
  private retrieveFromCookies(): StoredTokens | null {
    if (!this.cookieStorage) return null;

    const accessToken = this.cookieStorage.get(this.ACCESS_TOKEN_KEY);
    const refreshToken = this.cookieStorage.get(this.REFRESH_TOKEN_KEY);
    const metadataStr = this.cookieStorage.get(this.TOKEN_METADATA_KEY);

    // The refresh token lives in a separate httpOnly cookie managed by the
    // backend (JS can't read it), so only the access token is required here.
    if (!accessToken) return null;

    try {
      const metadata = metadataStr ? JSON.parse(metadataStr) : {};
      return {
        accessToken,
        refreshToken: refreshToken || '',
        expiresAt: metadata.expiresAt || Date.now() + 3600000,
        tokenType: metadata.tokenType || 'Bearer',
        scope: metadata.scope,
      };
    } catch {
      return null;
    }
  }

  /**
   * Store tokens to web storage (localStorage/sessionStorage)
   */
  private async storeToWebStorage(tokens: StoredTokens): Promise<void> {
    const storage = this.config.storageType === 'localStorage' 
      ? localStorage 
      : sessionStorage;

    if (this.config.enableEncryption && this.secureStorage) {
      // Store encrypted
      await this.secureStorage.setSecure(this.ACCESS_TOKEN_KEY, tokens.accessToken);
      await this.secureStorage.setSecure(this.REFRESH_TOKEN_KEY, tokens.refreshToken);
      storage.setItem(this.TOKEN_METADATA_KEY, JSON.stringify({
        expiresAt: tokens.expiresAt,
        tokenType: tokens.tokenType,
        scope: tokens.scope,
      }));
    } else {
      // Store unencrypted (not recommended)
      storage.setItem(this.ACCESS_TOKEN_KEY, tokens.accessToken);
      storage.setItem(this.REFRESH_TOKEN_KEY, tokens.refreshToken);
      storage.setItem(this.TOKEN_METADATA_KEY, JSON.stringify({
        expiresAt: tokens.expiresAt,
        tokenType: tokens.tokenType,
        scope: tokens.scope,
      }));
    }
  }

  /**
   * Retrieve tokens from web storage
   */
  private async retrieveFromWebStorage(): Promise<StoredTokens | null> {
    const storage = this.config.storageType === 'localStorage' 
      ? localStorage 
      : sessionStorage;

    try {
      let accessToken: string | null;
      let refreshToken: string | null;

      if (this.config.enableEncryption && this.secureStorage) {
        // Retrieve encrypted
        accessToken = await this.secureStorage.getSecure(this.ACCESS_TOKEN_KEY);
        refreshToken = await this.secureStorage.getSecure(this.REFRESH_TOKEN_KEY);
      } else {
        // Retrieve unencrypted
        accessToken = storage.getItem(this.ACCESS_TOKEN_KEY);
        refreshToken = storage.getItem(this.REFRESH_TOKEN_KEY);
      }

      // Refresh token is cookie-managed by the backend; only the access token
      // is required to consider the session restorable here.
      if (!accessToken) return null;

      const metadataStr = storage.getItem(this.TOKEN_METADATA_KEY);
      const metadata = metadataStr ? JSON.parse(metadataStr) : {};

      return {
        accessToken,
        refreshToken: refreshToken || '',
        expiresAt: metadata.expiresAt || Date.now() + 3600000,
        tokenType: metadata.tokenType || 'Bearer',
        scope: metadata.scope,
      };
    } catch {
      return null;
    }
  }

  /**
   * Emit storage event for cross-tab synchronization
   */
  private emitStorageEvent(action: string, data: StoredTokens | TokenPair | null): void {
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      const event = new CustomEvent('token-storage', {
        detail: { action, data, timestamp: Date.now() },
      });
      window.dispatchEvent(event);
    }
  }

  /**
   * Listen for storage events from other tabs
   */
  onStorageChange(
    callback: (
      action: string,
      data: StoredTokens | TokenPair | { key: string; newValue: string | null } | null
    ) => void
  ): () => void {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent;
      callback(customEvent.detail.action, customEvent.detail.data);
    };

    window.addEventListener('token-storage', handler);
    
    // Also listen for native storage events
    const storageHandler = (event: StorageEvent) => {
      if (event.key?.startsWith(this.config.tokenPrefix)) {
        callback('external-change', { key: event.key, newValue: event.newValue });
      }
    };
    
    window.addEventListener('storage', storageHandler);

    // Return cleanup function
    return () => {
      window.removeEventListener('token-storage', handler);
      window.removeEventListener('storage', storageHandler);
    };
  }

  /**
   * Get storage statistics
   */
  getStats(): {
    storageType: string;
    hasTokens: boolean;
    cacheSize: number;
    isEncrypted: boolean;
  } {
    return {
      storageType: this.config.storageType,
      hasTokens: this.memoryCache.has('tokens'),
      cacheSize: this.memoryCache.size(),
      isEncrypted: this.config.enableEncryption,
    };
  }

  /**
   * Update storage configuration
   */
  async updateConfig(newConfig: Partial<TokenStorageConfig>): Promise<void> {
    // Clear existing tokens before changing storage type
    if (newConfig.storageType && newConfig.storageType !== this.config.storageType) {
      await this.clear();
    }

    this.config = { ...this.config, ...newConfig };
  }
}